import React from "react";
import { motion } from "framer-motion";
import { useT } from "@/i18n/LanguageContext";

// Bespoke Aperina "Moment" section — a single cinematic art photograph paired
// with an editorial caption. Sits between highlights and product-line on the
// Aperina brand page. Cream canvas + burgundy/gold typography to continue the
// ornate hero's visual language. Everything is purely presentational; no
// interaction beyond the ambient parallax-lite scale on scroll into view.
const BURGUNDY = "#5B1A2D";
const GOLD = "#B78535";
const CREAM = "#FBF3E7";

export default function AperinaMoment({ image }) {
  const { t } = useT();
  if (!image) return null;

  const trSteps = t("brandContent.aperina.steps");
  const steps = Array.isArray(trSteps) && trSteps.length === 3
    ? trSteps
    : [
        "Fill a wine glass with ice",
        "Pour Aperina, top with soda and spirit preferred",
        "Finish with an orange slice",
      ];

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: CREAM, color: BURGUNDY }}
      data-testid="aperina-moment"
    >
      {/* Ambient corner mandala — very low opacity */}
      <img
        aria-hidden
        src="/aperina/aperina-mandala.png"
        alt=""
        className="pointer-events-none select-none absolute left-[-10%] top-[-6%] w-[46%] max-w-none opacity-[0.05]"
      />

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* LEFT — editorial title / body */}
          <div className="lg:col-span-4 order-2 lg:order-1">
            <p
              className="text-[0.7rem] md:text-xs font-bold uppercase mb-5"
              style={{ color: GOLD, letterSpacing: "0.36em" }}
            >
              {t("brandPage.fromDeck", { name: "Aperina" })}
            </p>
            <h2
              className="font-editorial italic leading-[1.05] text-[clamp(2.25rem,4.4vw,3.75rem)]"
              style={{ color: BURGUNDY }}
            >
              {t("brandContent.aperina.momentTitle") || "A golden-hour ritual."}
            </h2>

            <div
              className="mt-6 h-px w-24"
              style={{
                background: `linear-gradient(90deg, ${GOLD}, transparent)`,
              }}
            />

            {/* Serving ritual — three-step editorial cue, moved here so it sits
                alongside the cinematic art photograph rather than the emblem. */}
            <ol className="mt-10 space-y-6">
              {[
                { n: "I", label: steps[0] },
                { n: "II", label: steps[1] },
                { n: "III", label: steps[2] },
              ].map((step) => (
                <li
                  key={step.n}
                  className="border-t pt-4"
                  style={{ borderColor: `${GOLD}55` }}
                >
                  <span
                    className="font-editorial italic text-3xl block"
                    style={{ color: GOLD }}
                  >
                    {step.n}.
                  </span>
                  <span
                    className="mt-1 block text-base leading-snug max-w-xs"
                    style={{ color: BURGUNDY }}
                  >
                    {step.label}
                  </span>
                </li>
              ))}
            </ol>
          </div>

          {/* RIGHT — cinematic art photograph */}
          <div className="lg:col-span-8 order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.98 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden rounded-3xl shadow-[0_30px_80px_-25px_rgba(91,26,45,0.4)]"
              style={{ border: `1px solid ${GOLD}33` }}
            >
              <img
                src={image}
                alt="Aperina served in a wine glass with soda, ice and orange — golden-hour Mediterranean setting"
                data-testid="aperina-moment-image"
                className="w-full h-auto block"
              />
            </motion.div>

            {/* Under-image caption — subtle italic credit line */}
            <p
              className="mt-4 text-center italic font-editorial text-sm"
              style={{ color: `${BURGUNDY}88` }}
            >
              {t("brandContent.aperina.momentCaption") ||
                "— Aperina, Original Recipe. Served over ice with soda & orange."}
            </p>
          </div>
        </div>
      </div>

      {/* Fine bottom divider — signature gold hairline */}
      <div
        className="h-[3px] w-full"
        style={{
          background: `linear-gradient(90deg, transparent 5%, ${GOLD} 20%, ${GOLD} 80%, transparent 95%)`,
        }}
      />
    </section>
  );
}
